/**
 * useNotes / useNote / mutation hooks
 *
 * Local-first pattern using SQLite WASM + reactive queries:
 *  - Reads: useSQLiteQuery subscribes to table changes — any write to
 *    SQLite automatically triggers a re-render via the notification layer.
 *  - Writes: write to SQLite immediately → reactive layer updates UI.
 *    If offline, the op is queued in sync_queue for the next reconnect.
 *    If online, sync to the server in the background.
 */

import { startTransition, useCallback, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useSQLiteQuery, useFingerprintedQuery } from '../lib/reactive'
import { computeNoteLimitState, countActiveNotes, useEffectiveNoteLimit } from './useNoteLimit'
import { useAuthStore } from '../store/auth'
import { useUIStore, type NoteSortField, type NoteSortDirection } from '../store/ui'
import { apiFetch } from '../api/client'
import { track, AnalyticsEvent } from '../lib/analytics'
import {
  putNote,
  updateNote,
  softDeleteNote,
  leaveSharedNote,
  enqueueSyncOp,
  hasPendingCreate,
  getNote as dbGetNote,
  type LocalNote,
  type LocalTag,
} from '../lib/db'
import { listActiveNoteTitles, rowToLocalNote, type NoteRow } from '../lib/repositories'
import { query, queryOne, execute, transaction, notifyChanges, subscribeToChanges, isDbReady } from '../lib/sqlite'
import { noteEffectiveOrder, parseSearchQuery } from '@caliu/utils'
import { compileSearchSql } from '../lib/searchSql'
import type { Note } from '../types'
import { getSharingFeature } from '../lib/noteEngineState'
import { SHARED_BY_ME_SQL, SHARED_WITH_ME_SQL, isSharedWithMe } from '../lib/sharing'

// Epoch-ms of a note's created_at, computed in SQLite. This is the fallback
// order key for the "Manual" sort mode when sort_order is NULL. It MUST stay
// identical to noteEffectiveOrder's JS fallback (Date.parse) and the Swift SQL.
// julianday('1970-01-01') = 2440587.5; there are 86_400_000 ms per day.
const CREATED_AT_EPOCH_MS_SQL = "(julianday(created_at) - 2440587.5) * 86400000"

// ── Helpers ───────────────────────────────────────────────────────────────

export type NoteView =
  | 'all'
  | 'pinned'
  | 'todo'
  | 'today'
  | 'untagged'
  | 'thisWeek'
  | 'shared'
  | 'sharedWithMe'
  | 'attachments'

export function sortNotes<T extends Note>(
  notes: T[],
  sortField: NoteSortField = 'updatedAt',
  sortDirection: NoteSortDirection = 'desc',
): T[] {
  const dir = sortDirection === 'asc' ? 1 : -1
  return [...notes].sort((a, b) => {
    // Pinned notes always come first
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1

    if (sortField === 'manual') {
      // Higher effective order = higher in the list (DESC).
      return noteEffectiveOrder(b) - noteEffectiveOrder(a)
    }
    if (sortField === 'title') {
      return dir * a.title.localeCompare(b.title)
    }
    const dateA = new Date(a[sortField]).getTime()
    const dateB = new Date(b[sortField]).getTime()
    return dir * (dateA - dateB)
  })
}

/** Sort direction is fixed per field (the order toggle was removed): date
 *  fields sort newest-first, title sorts A-Z. Exported so every surface that
 *  renders a list of notes (note list, command palette) picks the same
 *  direction from the same field. */
export function noteSortDirectionFor(field: NoteSortField): NoteSortDirection {
  return field === 'title' ? 'asc' : 'desc'
}

// ── Queries ───────────────────────────────────────────────────────────────

// Map sort field to SQL column name
function sortFieldToColumn(field: NoteSortField): string {
  switch (field) {
    case 'createdAt': return 'created_at'
    case 'title': return 'title'
    default: return 'updated_at'
  }
}

/** Full note objects in display order. Delegates ordering to useNoteIds
 *  so the filter/sort logic is defined in one place. */
export function useNotes(tagId?: string, view?: NoteView): LocalNote[] {
  const ids = useNoteIds(tagId, view)
  // Use a string fingerprint as the dep instead of the ids array reference,
  // which changes on every useNoteIds re-render even when the list is identical.
  const idsFingerprint = ids.join(',')

  const notes = useSQLiteQuery(
    () => {
      if (ids.length === 0) return []
      // Batch-fetch by IDs, then restore the ordering from useNoteIds
      const placeholders = ids.map(() => '?').join(',')
      const rows = query<NoteRow>(
        `SELECT * FROM notes WHERE id IN (${placeholders})`,
        ids,
      )
      const byId = new Map(rows.map((r) => [r.id, rowToLocalNote(r)]))
      return ids.map((id) => byId.get(id)).filter((n): n is LocalNote => !!n)
    },
    [idsFingerprint],
    ['notes', 'note_tags'],
    [] as LocalNote[],
  ) ?? []

  return notes
}

/** Search note content via SQL LIKE — used by CommandPalette for full-text
 *  search without subscribing to reactive updates. Returns IDs only. */
/**
 * The palette's note search: `q` in the search query language
 * (`compileSearchSql`), active notes only, pinned then most recent, at most
 * `limit` that are not in `excludeIds`. The palette re-sorts them.
 */
export function searchNoteIdsByContent(q: string, excludeIds: Set<string>, limit: number): string[] {
  if (q.trim().length < 2) return []
  const tags = query<{ id: string; name: string; parentId: string | null }>(
    "SELECT id, name, parent_id AS parentId FROM tags",
  )
  const compiled = compileSearchSql(parseSearchQuery(q), { tags, now: new Date() }, limit + excludeIds.size)
  if (!compiled) return []
  return query<{ id: string }>(compiled.sql, compiled.params)
    .filter((r) => !excludeIds.has(r.id))
    .slice(0, limit)
    .map((r) => r.id)
}

/** Minimal note data (id + title) for suggestion dropdowns like [[mentions]].
 *  Uses fingerprint to skip re-renders when only content/updatedAt changes
 *  (title changes are rare during continuous editing). */
export function useNoteTitles(): { id: string; title: string }[] {
  return useFingerprintedQuery<{ id: string; title: string }[]>({
    tables: ['notes'],
    fingerprint: () => {
      const rows = query<{ id: string; title: string }>(
        "SELECT id, title FROM notes WHERE deleted_at IS NULL AND archived_at IS NULL ORDER BY updated_at DESC",
      )
      return rows.map((r) => `${r.id}:${r.title}`).join('|')
    },
    query: listActiveNoteTitles,
  })
}

/** Query note IDs in display order — pure function, no hooks. */
function queryNoteIds(
  tagId: string | undefined,
  view: NoteView | undefined,
  sortField: NoteSortField,
  sortDirection: NoteSortDirection,
): string[] {
  const col = sortFieldToColumn(sortField)
  const collate = sortField === 'title' ? ' COLLATE NOCASE' : ''
  const orderDir = sortDirection === 'desc' ? 'DESC' : 'ASC'
  // Manual mode orders by the explicit sort_order, falling back to created_at
  // (newest first). DESC so a higher sort_order / newer note is at the top.
  const orderExpr =
    sortField === 'manual'
      ? `COALESCE(sort_order, ${CREATED_AT_EPOCH_MS_SQL}) DESC`
      : `${col}${collate} ${orderDir}`
  const filterRowsByTag = <T extends { id: string }>(rows: T[]): T[] => {
    if (!tagId) return rows

    const noteIdsWithTag = new Set(
      query<{ note_id: string }>('SELECT note_id FROM note_tags WHERE tag_id = ?', [tagId])
        .map((r) => r.note_id),
    )
    return rows.filter((r) => noteIdsWithTag.has(r.id))
  }

  let sql: string
  const params: unknown[] = []

  // Every active-list view excludes archived notes (archived_at IS NULL) the
  // same way it excludes trashed ones (deleted_at IS NULL). Archived notes are
  // only ever shown in the dedicated Archive view (useArchiveNotes), never in
  // All Notes, smart lists, or tag/folder views.
  switch (view) {
    case 'pinned':
      sql = `SELECT id FROM notes WHERE is_pinned = 1 AND deleted_at IS NULL AND archived_at IS NULL ORDER BY ${orderExpr}`
      break
    case 'todo':
      sql = `SELECT id FROM notes
             WHERE deleted_at IS NULL AND archived_at IS NULL AND content LIKE '%- [ ]%'
             ORDER BY is_pinned DESC, ${orderExpr}`
      break
    case 'today': {
      const todayStart = new Date()
      todayStart.setHours(0, 0, 0, 0)
      sql = `SELECT id FROM notes WHERE updated_at >= ? AND deleted_at IS NULL AND archived_at IS NULL ORDER BY is_pinned DESC, ${orderExpr}`
      params.push(todayStart.toISOString())
      break
    }
    case 'untagged':
      sql = `SELECT id FROM notes
             WHERE deleted_at IS NULL AND archived_at IS NULL
               AND id NOT IN (SELECT note_id FROM note_tags)
             ORDER BY is_pinned DESC, ${orderExpr}`
      break
    case 'thisWeek': {
      const weekAgo = new Date()
      weekAgo.setDate(weekAgo.getDate() - 7)
      weekAgo.setHours(0, 0, 0, 0)
      sql = `SELECT id FROM notes WHERE updated_at >= ? AND deleted_at IS NULL AND archived_at IS NULL ORDER BY is_pinned DESC, ${orderExpr}`
      params.push(weekAgo.toISOString())
      break
    }
    case 'shared':
      if (getSharingFeature()) {
        // "Shared": this account's notes someone else can open (people or the link).
        sql = `SELECT id FROM notes WHERE ${SHARED_BY_ME_SQL} AND deleted_at IS NULL AND archived_at IS NULL ORDER BY is_pinned DESC, ${orderExpr}`
        params.push(useAuthStore.getState().user?.id ?? '')
      } else {
        // Before the beta: the public link alone, as it was.
        sql = `SELECT id FROM notes WHERE shared_at IS NOT NULL AND deleted_at IS NULL AND archived_at IS NULL ORDER BY is_pinned DESC, ${orderExpr}`
      }
      break
    case 'sharedWithMe':
      // Notes another account shared with this one (PLAN.md task 3.5).
      sql = `SELECT id FROM notes WHERE ${SHARED_WITH_ME_SQL} AND deleted_at IS NULL AND archived_at IS NULL ORDER BY is_pinned DESC, ${orderExpr}`
      params.push(useAuthStore.getState().user?.id ?? '')
      break
    case 'attachments':
      sql = `SELECT id FROM notes
             WHERE deleted_at IS NULL AND archived_at IS NULL
               AND EXISTS (
                 SELECT 1
                 FROM attachment_projection ap
                 WHERE ap.note_id = notes.id
               )
             ORDER BY is_pinned DESC, ${orderExpr}`
      break
    default: {
      // All Notes also surfaces notes shared with the user by someone else
      // (shared_note_refs pointer + shared_notes content cache). They are
      // read-only stubs — never pinned, ordered by the cached updated_at
      // (falling back to added_at until the public fetch lands). Skipped for
      // tag views (tagId set): stubs have no note_tags rows so the tag filter
      // would drop them anyway.
      // Only while `sharing` is off: with it on, shared notes are rows of
      // `notes` and the refs are kept for old builds alone.
      const userId = useAuthStore.getState().user?.id
      if (userId && !tagId && !getSharingFeature()) {
        sql = `SELECT id FROM (
                 SELECT id, is_pinned, title, created_at, updated_at, sort_order
                 FROM notes WHERE deleted_at IS NULL AND archived_at IS NULL
                 UNION ALL
                 SELECT snr.note_id AS id, 0 AS is_pinned,
                        COALESCE(sn.title, '') AS title,
                        COALESCE(sn.created_at, snr.added_at) AS created_at,
                        COALESCE(sn.updated_at, snr.added_at) AS updated_at,
                        NULL AS sort_order
                 FROM shared_note_refs snr
                 LEFT JOIN shared_notes sn ON sn.id = snr.note_id
                 WHERE snr.user_id = ?
               ) ORDER BY is_pinned DESC, ${orderExpr}`
        params.push(userId)
      } else {
        sql = `SELECT id FROM notes WHERE deleted_at IS NULL AND archived_at IS NULL ORDER BY is_pinned DESC, ${orderExpr}`
      }
    }
  }

  let rows = query<{ id: string }>(sql, params.length > 0 ? params : undefined)
  rows = filterRowsByTag(rows)

  return rows.map((r) => r.id)
}

/** Lightweight query that returns only note IDs in display order.
 *  Only triggers a React re-render when the ID list actually changes
 *  (note added, deleted, or reordered) — not on content/title edits. */
export function useNoteIds(tagId?: string, view?: NoteView): string[] {
  const noteSortField = useUIStore((s) => s.noteSortField)

  // Refs to capture latest values for the subscription callback
  const argsRef = useRef({ tagId, view, noteSortField })
  argsRef.current = { tagId, view, noteSortField }

  const versionRef = useRef(0)
  const fingerprintRef = useRef<string>('')
  const dataRef = useRef<string[]>([])

  const subscribe = useCallback((onStoreChange: () => void) => {
    return subscribeToChanges((changedTables) => {
      if (!isDbReady()) return
      if (
        !changedTables.has('notes') &&
        !changedTables.has('note_tags') &&
        !changedTables.has('tags') &&
        !changedTables.has('attachment_projection') &&
        !changedTables.has('shared_note_refs') &&
        !changedTables.has('shared_notes')
      ) return
      const { tagId: t, view: v, noteSortField: sf } = argsRef.current
      const ids = queryNoteIds(t, v, sf, noteSortDirectionFor(sf))
      const fp = ids.join(',')
      if (fp === fingerprintRef.current) return // IDs unchanged — skip re-render
      fingerprintRef.current = fp
      dataRef.current = ids
      versionRef.current += 1
      startTransition(() => onStoreChange())
    })
  }, [])

  const getSnapshot = useCallback(() => versionRef.current, [])
  const version = useSyncExternalStore(subscribe, getSnapshot)

  return useMemo(() => {
    if (!isDbReady()) return []
    const { tagId: t, view: v, noteSortField: sf } = argsRef.current
    const ids = queryNoteIds(t, v, sf, noteSortDirectionFor(sf))
    fingerprintRef.current = ids.join(',')
    dataRef.current = ids
    return ids
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, tagId, view, noteSortField])
}

/** Subscribe to a single note for the editor.
 *  Fingerprint includes updated_at so an external sync pull (which rewrites
 *  content + bumps updated_at on an already-synced note) re-renders the
 *  editor and lets NoteEditor's isExternalUpdate branch reload the doc.
 *  A self-edit bumps updated_at once per save (the pending write), so this
 *  costs at most one interruptible re-render per 800ms autosave — the
 *  isExternalUpdate guard (syncStatus === 'synced') no-ops it while editing. */
export function useNote(id: string | null): { note: LocalNote | undefined; isLoading: boolean } {
  const note = useFingerprintedQuery<LocalNote | undefined>({
    tables: ['notes', 'note_tags'],
    fingerprint: () => {
      if (!id) return 'none'
      const row = queryOne<{ is_pinned: number; deleted_at: string | null; shared_at: string | null; updated_at: string; is_read_only: number; share: string }>(
        `SELECT is_pinned, deleted_at, shared_at, updated_at, is_read_only,
           COALESCE(owner_id, '') || ':' || COALESCE(share_role, '') || ':' || member_count || ':' || COALESCE(link_access, '') AS share
         FROM notes WHERE id = ?`,
        [id],
      )
      const tagCount = queryOne<{ cnt: number }>(
        'SELECT COUNT(*) as cnt FROM note_tags WHERE note_id = ?',
        [id],
      )
      // is_read_only is in the fingerprint so toggling it re-renders the editor
      // (it flips Tiptap's editable flag). A metadata-only update never bumps
      // updated_at, so without it the toggle would not be observed.
      return row
        ? `${row.is_pinned}|${row.deleted_at}|${row.shared_at}|${tagCount?.cnt ?? 0}|${row.updated_at}|${row.is_read_only}|${row.share}`
        : 'gone'
    },
    query: () => {
      if (!id) return undefined
      return dbGetNote(id) ?? undefined
    },
    deps: [id],
  })

  return { note, isLoading: false }
}

/** Batch-fetch notes by IDs with a single subscription.
 *  Returns a Map<id, LocalNote> and only re-renders when the fingerprint
 *  (concatenated updated_at values) actually changes — one cheap query
 *  instead of N per-item subscriptions. */
export function useNotesMap(ids: string[]): Map<string, LocalNote> {
  const idsRef = useRef(ids)
  idsRef.current = ids
  const versionRef = useRef(0)
  const fpMapRef = useRef(new Map<string, string>())
  const changedIdsRef = useRef(new Set<string>())

  const subscribe = useCallback((onStoreChange: () => void) => {
    return subscribeToChanges((changedTables) => {
      if (!isDbReady()) return
      if (!changedTables.has('notes') && !changedTables.has('note_tags')) return
      const currentIds = idsRef.current
      if (currentIds.length === 0) return
      const placeholders = currentIds.map(() => '?').join(',')
      const rows = query<{ id: string; updated_at: string; is_pinned: number; share: string }>(
        `SELECT id, updated_at, is_pinned,
           COALESCE(owner_id, '') || ':' || COALESCE(share_role, '') || ':' || member_count || ':' || COALESCE(link_access, '') || ':' || COALESCE(shared_at, '') AS share
         FROM notes WHERE id IN (${placeholders})`,
        currentIds,
      )
      // Diff fingerprints to find which notes actually changed (the share
      // fields drive the list's shared badge)
      const newFpMap = new Map(rows.map((r) => [r.id, `${r.updated_at}:${r.is_pinned}:${r.share}`]))
      const changed = new Set<string>()
      for (const [id, fp] of newFpMap) {
        if (fpMapRef.current.get(id) !== fp) changed.add(id)
      }
      for (const id of fpMapRef.current.keys()) {
        if (!newFpMap.has(id)) changed.add(id)
      }
      if (changed.size === 0) return
      fpMapRef.current = newFpMap
      changedIdsRef.current = changed
      versionRef.current += 1
      startTransition(() => onStoreChange())
    })
  }, [])

  const getSnapshot = useCallback(() => versionRef.current, [])
  const version = useSyncExternalStore(subscribe, getSnapshot)

  const prevMapRef = useRef<Map<string, LocalNote>>(new Map())

  return useMemo(() => {
    if (!isDbReady() || ids.length === 0) return new Map<string, LocalNote>()
    const prev = prevMapRef.current
    const changed = changedIdsRef.current

    // Determine which IDs need fetching (changed + new)
    const toFetch = ids.filter((id) => changed.has(id) || !prev.has(id))

    // Copy unchanged from previous map
    const next = new Map<string, LocalNote>()
    for (const id of ids) {
      if (!changed.has(id) && prev.has(id)) {
        next.set(id, prev.get(id)!)
      }
    }

    // Fetch only the changed/new notes
    if (toFetch.length > 0) {
      const placeholders = toFetch.map(() => '?').join(',')
      const rows = query<NoteRow>(
        `SELECT * FROM notes WHERE id IN (${placeholders})`,
        toFetch,
      )
      for (const row of rows) {
        next.set(row.id, rowToLocalNote(row))
      }
    }

    prevMapRef.current = next
    changedIdsRef.current = new Set()
    return next
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, ids])
}

// ── useCreateNote ─────────────────────────────────────────────────────────

/** Writes a new note locally and enqueues its create op. No limit check: this
 *  is the raw write, shared by the guarded hook below. */
export function createNoteRecord(
  data: { title?: string; content: string; tags?: string[] },
  source = 'unknown',
): Note {
  const now = new Date().toISOString()
  const id = crypto.randomUUID()
  const localNote: LocalNote = {
    id,
    userId: '',
    title: data.title ?? '',
    content: data.content,
    isPinned: false,
    isEncrypted: false,
    wordCount: (data.content || '').split(/\s+/).filter(Boolean).length,
    tags: data.tags ?? [],
    createdAt: now,
    updatedAt: now,
    syncStatus: 'pending',
  }

  // 1. Write to local SQLite immediately
  putNote(localNote)

  // 2. Enqueue for sync engine — only send content, title is derived server-side.
  // Pass the local create timestamps so the worker stores the same
  // `updated_at` we have locally. Without this, an offline create
  // that pushes hours later gets stamped with the server's `now`,
  // and the very next edit's `expected_updated_at` (our local create
  // time) trips a false conflict.
  const { title: _title, ...syncData } = data
  enqueueSyncOp({
    type: 'create',
    entity: 'note',
    entityId: id,
    data: { ...syncData, id, created_at: now, updated_at: now },
    timestamp: Date.now(),
    retries: 0,
  })

  // The worker's create op ignores `tags`, and the tags-sync endpoint
  // refuses a note whose create is still queued, so link them the way
  // Swift does: one note_tag op per tag, queued behind the create.
  // Without this a tagged note the user never edits syncs untagged.
  for (const tagId of data.tags ?? []) {
    enqueueSyncOp({
      type: 'create',
      entity: 'note_tag',
      entityId: `${id}:${tagId}`,
      data: { note_id: id, tag_id: tagId },
      timestamp: Date.now(),
      retries: 0,
    })
  }

  // Value Moment — the core activation action for a notes app.
  track(AnalyticsEvent.NoteCreated, { source })

  return localNote as Note
}

/**
 * The free note limit guard (see `useNoteLimit.ts`): at the cap the note is
 * not created, the `note_limit` paywall opens, and `null` comes back so the
 * caller does not navigate. Pure over its inputs so the guard is testable
 * without a React tree.
 */
export function createNoteWithLimitGuard(
  reached: boolean,
  data: { title?: string; content: string; tags?: string[] },
  source = 'unknown',
): Note | null {
  if (reached) {
    useUIStore.getState().openPaywall('note_limit')
    return null
  }
  return createNoteRecord(data, source)
}

/** Creates a note, or opens the `note_limit` paywall and resolves to `null`
 *  when the free note limit is reached. Callers must handle `null` (no
 *  navigation, no prefetch). */
export function useCreateNote() {
  const [isPending, setIsPending] = useState(false)
  const limit = useEffectiveNoteLimit()
  const mutateAsync = async (
    data: { title?: string; content: string; tags?: string[] },
    source = 'unknown',
  ): Promise<Note | null> => {
    setIsPending(true)
    try {
      // The count is read at call time, not render time, so a burst of
      // mod+n before React re-renders cannot slip past the cap.
      const { reached } = computeNoteLimitState(limit, countActiveNotes())
      return createNoteWithLimitGuard(reached, data, source)
    } finally {
      setIsPending(false)
    }
  }
  return { mutateAsync, isPending }
}

// ── useUpdateNote ─────────────────────────────────────────────────────────

export function useUpdateNote() {
  const [isPending, setIsPending] = useState(false)
  const mutate = async (
    { id, ...data }: Partial<Note> & { id: string },
    options: { preserveHistory?: boolean } = {},
  ) => {
    setIsPending(true)
    try {
      const existing = dbGetNote(id)
      const expectedUpdatedAt = existing?.updatedAt
      const localUpdatedAt = new Date().toISOString()

      // 1. Write to local SQLite immediately — UI updates via reactive queries
      updateNote(id, { ...data, updatedAt: localUpdatedAt, syncStatus: 'pending' })

      // 2. Enqueue for sync engine — exclude title (derived server-side from content)
      const { title: _title, ...syncData } = data
      const opData: Record<string, unknown> = {
        ...syncData,
        updated_at: localUpdatedAt,
        expected_updated_at: expectedUpdatedAt,
      }
      // Opt out of the worker's 5-minute compaction window so the
      // resulting sync_log entry — and the previous one — both stick
      // around. Used by the history-restore flow.
      if (options.preserveHistory) {
        opData.preserve_history = true
      }
      enqueueSyncOp({
        type: 'update',
        entity: 'note',
        entityId: id,
        data: opData,
        timestamp: Date.now(),
        retries: 0,
      })
    } finally {
      setIsPending(false)
    }
  }
  return { mutate, isPending }
}

// ── usePinNote ────────────────────────────────────────────────────────────

export function usePinNote() {
  const [isPending, setIsPending] = useState(false)
  const mutate = async ({
    id,
    isPinned,
    source = 'note_actions_menu',
  }: {
    id: string
    isPinned: boolean
    source?: string
  }) => {
    setIsPending(true)
    try {
      const expectedUpdatedAt = dbGetNote(id)?.updatedAt
      updateNote(id, { isPinned, syncStatus: 'pending' })
      enqueueSyncOp({
        type: 'update',
        entity: 'note',
        entityId: id,
        data: { is_pinned: isPinned, expected_updated_at: expectedUpdatedAt },
        timestamp: Date.now(),
        retries: 0,
      })
      track(AnalyticsEvent.NoteAction, { action: isPinned ? 'pin' : 'unpin', source })
    } finally {
      setIsPending(false)
    }
  }
  return { mutate, isPending }
}

// ── useSetNoteReadOnly ────────────────────────────────────────────────────

/** Flips a note's read-only guard ("Make Read-Only" / "Make Editable").
 *  Mirrors usePinNote: an update sync op carrying `is_read_only`, metadata
 *  only, so it must NOT bump updated_at (toggling must never reorder the note
 *  in "recently updated" nor trip a false single-device conflict). */
export function useSetNoteReadOnly() {
  const mutate = useCallback(({ id, isReadOnly }: { id: string; isReadOnly: boolean }) => {
    // Persist any pending 800ms autosave FIRST so this toggle reads the note's
    // current updated_at from SQLite. deduplicateSyncOps now merges two update
    // ops for the same note field-by-field, so a content save racing this
    // toggle no longer drops either side; the flush is kept as the same
    // belt-and-suspenders guard tag rename / history restore use. The Swift
    // toggles flush too.
    window.dispatchEvent(new Event("caliu-flush-autosave"))
    const expectedUpdatedAt = dbGetNote(id)?.updatedAt
    updateNote(id, { isReadOnly, syncStatus: 'pending' })
    enqueueSyncOp({
      type: 'update',
      entity: 'note',
      entityId: id,
      data: { is_read_only: isReadOnly, expected_updated_at: expectedUpdatedAt },
      timestamp: Date.now(),
      retries: 0,
    })
  }, [])
  return { mutate }
}

// ── useSetNoteOrder ───────────────────────────────────────────────────────

/** Writes a note's manual sort_order (epoch-ms scale) and enqueues an
 *  update sync op. Mirrors usePinNote: metadata-only, so it does NOT bump
 *  updated_at (a reorder must never reorder "recently updated"). */
export function useSetNoteOrder() {
  const mutate = useCallback(({ id, sortOrder }: { id: string; sortOrder: number }) => {
    const expectedUpdatedAt = dbGetNote(id)?.updatedAt
    updateNote(id, { sortOrder, syncStatus: 'pending' })
    enqueueSyncOp({
      type: 'update',
      entity: 'note',
      entityId: id,
      data: { sort_order: sortOrder, expected_updated_at: expectedUpdatedAt },
      timestamp: Date.now(),
      retries: 0,
    })
  }, [])
  return { mutate }
}

// ── useDeleteNote ─────────────────────────────────────────────────────────

export function useDeleteNote() {
  const [isPending, setIsPending] = useState(false)
  const mutate = async (id: string, source = 'note_actions_menu') => {
    setIsPending(true)
    try {
      // A member's delete means Leave (PLAN.md D5): never a trash of
      // someone else's note.
      if (isSharedWithMe(dbGetNote(id), useAuthStore.getState().user?.id)) {
        leaveSharedNote(id)
        track(AnalyticsEvent.SharedNoteLeft, { source })
        return
      }
      softDeleteNote(id)
      enqueueSyncOp({
        type: 'soft-delete',
        entity: 'note',
        entityId: id,
        timestamp: Date.now(),
        retries: 0,
      })
      track(AnalyticsEvent.NoteAction, { action: 'trash', source })
    } finally {
      setIsPending(false)
    }
  }
  return { mutate, isPending }
}

// ── useSyncNoteTags ───────────────────────────────────────────────────────

import { buildFullPathMap } from '../lib/tagHierarchy'

/**
 * The one funnel every tag is born in.
 *
 * There is no "new tag" button in Caliu — a tag comes into existence because a
 * `#hashtag` appeared in some note's markdown, whether the user typed it, the
 * tag menu inserted it, or an import brought it in. Every one of those paths
 * lands here, so this is where `tag_created` is reported. (The old call site
 * sat in a `useCreateTag` hook that nothing ever called, which is why the event
 * read zero while the database was gaining hundreds of tags a week.)
 *
 * `source` describes which of those paths ran; it defaults to the editor,
 * which is where the overwhelming majority of hashtags are typed.
 */
export function useSyncNoteTags() {
  const [isPending, setIsPending] = useState(false)
  const mutate = async ({
    noteId,
    tags,
    source = 'editor',
  }: {
    noteId: string
    tags: string[]
    source?: string
  }) => {
    setIsPending(true)
    try {
      const existingTags = query<{
        id: string; user_id: string; name: string; parent_id: string | null;
        color: string | null; icon: string | null; created_at: string; sync_status: string
      }>('SELECT * FROM tags')

      const localTags = existingTags.map((t) => ({
        id: t.id, userId: t.user_id, name: t.name,
        ...(t.parent_id ? { parentId: t.parent_id } : {}),
        color: t.color ?? undefined, icon: t.icon ?? undefined, createdAt: t.created_at,
        syncStatus: t.sync_status as LocalNote['syncStatus'],
      }) as LocalTag)
      const existingByFullPath = buildFullPathMap(localTags)

      const ids: string[] = []
      /** Tags this pass had to bring into existence, ancestors included. */
      const createdIds: string[] = []
      const now = new Date().toISOString()

      // Ensure a tag exists for a full path (e.g. "a/b/c"), creating ancestors as needed.
      // Returns the tag ID and adds all ancestors + the tag itself to ids[].
      function ensureTag(fullPath: string): string {
        const existing = existingByFullPath.get(fullPath)
        if (existing) {
          if (!ids.includes(existing.id)) ids.push(existing.id)
          return existing.id
        }

        // Recursively ensure parent exists first
        let parentId: string | null = null
        const slashIdx = fullPath.lastIndexOf('/')
        if (slashIdx > 0) {
          const parentPath = fullPath.substring(0, slashIdx)
          parentId = ensureTag(parentPath)
        }

        const leafName = slashIdx > 0 ? fullPath.substring(slashIdx + 1) : fullPath
        const newTagId = crypto.randomUUID()
        execute(
          `INSERT OR IGNORE INTO tags (id, user_id, name, parent_id, created_at, sync_status)
           VALUES (?, '', ?, ?, ?, 'pending')`,
          [newTagId, leafName, parentId, now],
        )
        const newTag = {
          id: newTagId, userId: '', name: leafName,
          parentId: parentId ?? undefined,
          createdAt: now, syncStatus: 'pending',
        } as LocalTag
        existingByFullPath.set(fullPath, newTag)
        ids.push(newTagId)
        createdIds.push(newTagId)
        return newTagId
      }

      for (const name of tags) {
        ensureTag(name.toLowerCase())
      }

      transaction(() => {
        // Update note_tags junction
        execute('DELETE FROM note_tags WHERE note_id = ?', [noteId])
        for (const tagId of ids) {
          execute('INSERT OR IGNORE INTO note_tags (note_id, tag_id) VALUES (?, ?)', [noteId, tagId])
        }

        // Orphan cleanup: remove tags not used by any non-deleted note and not a parent of another tag
        execute(`
          DELETE FROM tags WHERE id IN (
            SELECT t.id FROM tags t
            WHERE t.id NOT IN (
              SELECT DISTINCT nt.tag_id FROM note_tags nt
              JOIN notes n ON n.id = nt.note_id
              WHERE n.deleted_at IS NULL
            )
            AND t.id NOT IN (SELECT DISTINCT parent_id FROM tags WHERE parent_id IS NOT NULL)
          )
        `)
      })
      notifyChanges(['tags', 'note_tags'])

      // Report only the tags that survived the transaction: the orphan cleanup
      // above can drop one that ended up referenced by nothing. Reported before
      // the server reconciliation below, because that swaps local ids for
      // server ones without changing the fact that the tag is new.
      for (const createdId of createdIds) {
        if (queryOne<{ id: string }>('SELECT id FROM tags WHERE id = ?', [createdId])) {
          track(AnalyticsEvent.TagCreated, { source })
        }
      }

      // Sync to server — server is source of truth for tag IDs.
      // Not while the note is in conflict: its text is held until the user
      // resolves, and links must not reach the server ahead of the text they
      // derive from (an unlink could archive a tag the server's text still
      // has). Keep Server rewrites the links from the server's note; after
      // Keep Mine the next save runs this again.
      const inConflict =
        queryOne<{ sync_status: string }>('SELECT sync_status FROM notes WHERE id = ?', [noteId])
          ?.sync_status === 'conflict'
      if (navigator.onLine && !inConflict && !hasPendingCreate('note', noteId)) {
        const res = await apiFetch<{ tags: { id: string; name: string }[] }>(`/notes/${noteId}/tags/sync`, {
          method: 'POST',
          body: JSON.stringify({ tags }),
        })
        if (res?.tags) {
          // Reconcile: replace local pending tags with server tags (by name)
          transaction(() => {
            for (const serverTag of res.tags) {
              // Find local tag with same name but different ID
              const localDup = queryOne<{ id: string; parent_id: string | null }>(
                "SELECT id, parent_id FROM tags WHERE name = ? AND id != ? AND sync_status = 'pending'",
                [serverTag.name, serverTag.id],
              )
              if (localDup) {
                // Repoint child tags that reference the local ID
                execute('UPDATE tags SET parent_id = ? WHERE parent_id = ?', [serverTag.id, localDup.id])
                // Move note_tags references to server ID
                execute('UPDATE note_tags SET tag_id = ? WHERE tag_id = ?', [serverTag.id, localDup.id])
                // Insert server tag preserving the local parent_id, then delete local
                execute(
                  `INSERT OR IGNORE INTO tags (id, user_id, name, parent_id, color, icon, is_pinned, created_at, sync_status)
                   SELECT ?, user_id, name, parent_id, color, icon, is_pinned, created_at, 'synced' FROM tags WHERE id = ?`,
                  [serverTag.id, localDup.id],
                )
                execute('DELETE FROM tags WHERE id = ?', [localDup.id])
              }
              // Mark as synced if it already exists with the server ID
              execute("UPDATE tags SET sync_status = 'synced' WHERE id = ?", [serverTag.id])
            }
          })
          notifyChanges(['tags', 'note_tags'])
        }
      }
    } catch (err) {
      console.error('[syncTags] ERROR:', err)
    } finally {
      setIsPending(false)
    }
  }
  return { mutate, isPending }
}
